import { useRef } from "react";
import {
    motion,
    useInView,
    useMotionValue,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform,
} from "framer-motion";
import { Building2, Wifi } from "lucide-react";

const CX = 460;
const CY = 430;
const ISO = 0.866;
const EASE = [0.22, 1, 0.36, 1];

const P = (x, y, z = 0) => [CX + (x - y) * ISO, CY - z + (x + y) * 0.5];
const pts = (...ps) => ps.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");

const Box = ({ x, y, w, d, z0, h, t, l, r, stroke }) => {
    const a = w / 2;
    const b = d / 2;
    const pT = P(x - a, y - b, z0 + h);
    const pR = P(x + a, y - b, z0 + h);
    const pF = P(x + a, y + b, z0 + h);
    const pL = P(x - a, y + b, z0 + h);
    const drop = (p) => [p[0], p[1] + h];
    return (
        <g>
            <polygon points={pts(pL, pF, drop(pF), drop(pL))} fill={l} />
            <polygon points={pts(pF, pR, drop(pR), drop(pF))} fill={r} />
            <polygon
                points={pts(pT, pR, pF, pL)}
                fill={t}
                stroke={stroke || "none"}
                strokeWidth={stroke ? 1 : 0}
            />
        </g>
    );
};

const Shadow = ({ x, y, rx, ry, o = 0.07 }) => {
    const [cx, cy] = P(x, y, 26.4);
    return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="#16324F" opacity={o} filter="url(#soft)" />;
};

const Chair = ({ x, y, facing = 1 }) => {
    const [bx, by] = P(x, y, 27);
    const stemB = P(x, y, 30);
    const stemT = P(x, y, 47);
    return (
        <g>
            <ellipse cx={bx} cy={by} rx={15} ry={6} fill="#16324F" opacity={0.1} />
            <ellipse cx={bx} cy={by} rx={13} ry={5.5} fill="#46536F" />
            <line x1={stemB[0]} y1={stemB[1]} x2={stemT[0]} y2={stemT[1]} stroke="#5A688A" strokeWidth={3} />
            <Box x={x} y={y} w={24} d={24} z0={47} h={6} t="#66769A" l="#55648A" r="#48557A" />
            <Box x={x} y={y - 11 * facing} w={23} d={5} z0={53} h={34} t="#66769A" l="#55648A" r="#48557A" />
        </g>
    );
};

const Desk = ({ x, y, w = 220 }) => {
    const [cx, cy] = P(x, y, 26.4);
    return (
        <g>
            <ellipse cx={cx} cy={cy} rx={w / 2 + 14} ry={(w / 2 + 14) * 0.34} fill="#16324F" opacity={0.05} filter="url(#soft)" />
            {[-1, 1].map((sx) =>
                [-1, 1].map((sy) => (
                    <Box
                        key={`${sx}${sy}`}
                        x={x + sx * (w / 2 - 8)}
                        y={y + sy * 11}
                        w={6}
                        d={6}
                        z0={26}
                        h={44}
                        t="#AEB7CB"
                        l="#98A2BA"
                        r="#8791AC"
                    />
                )),
            )}
            <Box x={x} y={y} w={w} d={34} z0={70} h={7} t="#FBFCFE" l="#E9ECF3" r="#DDE1EC" stroke="#D5DAE6" />
            <Box x={x} y={y} w={w - 44} d={3} z0={77} h={16} t="rgba(206,221,245,0.55)" l="rgba(190,208,238,0.4)" r="rgba(180,198,230,0.35)" />
        </g>
    );
};

const Laptop = ({ x, y, z, flip = false }) => {
    const s = flip ? 1 : -1;
    const b1 = P(x - 15, y + 9 * s, z + 3);
    const b2 = P(x + 15, y + 9 * s, z + 3);
    const t1 = P(x - 14, y + 13 * s, z + 26);
    const t2 = P(x + 14, y + 13 * s, z + 26);
    return (
        <g>
            <Box x={x} y={y} w={30} d={20} z0={z} h={3} t="#D3D9E6" l="#B4BDd0" r="#A4AEC4" />
            <polygon points={pts(b1, b2, t2, t1)} fill="url(#screenGrad)" opacity={0.92} />
        </g>
    );
};

const Lamp = ({ x, y, z }) => {
    const b = P(x, y, z);
    const t = P(x, y, z + 26);
    return (
        <g>
            <line x1={b[0]} y1={b[1]} x2={t[0]} y2={t[1]} stroke="#8A94AC" strokeWidth={2.2} />
            <circle cx={t[0]} cy={t[1]} r={4.5} fill="#16324F" />
            <ellipse cx={t[0]} cy={t[1] + 18} rx={13} ry={5} fill="url(#glowGrad)" opacity={0.45} />
        </g>
    );
};

const PersonSeated = ({ x, y, color = "#33415C" }) => {
    const [bx, by] = P(x, y, 73);
    const [hx, hy] = P(x, y, 101);
    return (
        <g fill={color} opacity={0.92}>
            <rect x={bx - 10} y={by - 17} width={20} height={36} rx={10} />
            <circle cx={hx} cy={hy} r={8.5} />
        </g>
    );
};

const PersonStanding = ({ x, y, color = "#16324F" }) => {
    const [bx, by] = P(x, y, 62);
    const [hx, hy] = P(x, y, 112);
    return (
        <g fill={color} opacity={0.9}>
            <ellipse cx={P(x, y, 26.5)[0]} cy={P(x, y, 26.5)[1]} rx={13} ry={5} fill="#16324F" opacity={0.14} />
            <rect x={bx - 11} y={by - 36} width={22} height={72} rx={11} />
            <circle cx={hx} cy={hy} r={9.5} />
        </g>
    );
};

const Plant = ({ x, y, s = 1 }) => {
    const [cx, cy] = P(x, y, 64);
    return (
        <g>
            <ellipse cx={P(x, y, 26.5)[0]} cy={P(x, y, 26.5)[1]} rx={16 * s} ry={6 * s} fill="#16324F" opacity={0.08} />
            <Box x={x} y={y} w={20 * s} d={20 * s} z0={26} h={18} t="#D99B74" l="#C98B6A" r="#B57A58" />
            <ellipse cx={cx} cy={cy} rx={7 * s} ry={20 * s} fill="#3FA98C" transform={`rotate(-28 ${cx} ${cy})`} />
            <ellipse cx={cx} cy={cy} rx={7 * s} ry={24 * s} fill="#63BE9C" />
            <ellipse cx={cx} cy={cy} rx={7 * s} ry={20 * s} fill="#8FD3AE" transform={`rotate(28 ${cx} ${cy})`} />
        </g>
    );
};

const Cup = ({ x, y, z }) => {
    const [cx, cy] = P(x, y, z);
    return (
        <g>
            <rect x={cx - 4} y={cy - 9} width={8} height={9} rx={1.5} fill="#F4F6FA" stroke="#D8DEE9" strokeWidth={0.8} />
            <ellipse cx={cx} cy={cy - 9} rx={4} ry={1.8} fill="#FFFFFF" stroke="#D8DEE9" strokeWidth={0.8} />
        </g>
    );
};

const GlassPanel = ({ x1, y1, x2, y2 }) => {
    const b1 = P(x1, y1, 26);
    const b2 = P(x2, y2, 26);
    const t1 = P(x1, y1, 150);
    const t2 = P(x2, y2, 150);
    return (
        <g>
            <polygon
                points={pts(b1, b2, t2, t1)}
                fill="url(#glassGrad)"
                stroke="rgba(110,132,182,0.5)"
                strokeWidth={1.4}
            />
            <line x1={t1[0]} y1={t1[1]} x2={t2[0]} y2={t2[1]} stroke="rgba(255,255,255,0.85)" strokeWidth={1.6} />
        </g>
    );
};

const Whiteboard = () => {
    const b1 = P(-30, -217, 72);
    const b2 = P(140, -217, 72);
    const t1 = P(-30, -217, 152);
    const t2 = P(140, -217, 152);
    const l1 = P(2, -216.5, 120);
    const l2 = P(72, -216.5, 126);
    const l3 = P(2, -216.5, 102);
    const l4 = P(100, -216.5, 108);
    return (
        <g>
            <polygon points={pts(b1, b2, t2, t1)} fill="#FFFFFF" stroke="#D8DCE6" strokeWidth={1.5} />
            <line x1={l1[0]} y1={l1[1]} x2={l2[0]} y2={l2[1]} stroke="#7E35A8" strokeOpacity={0.4} strokeWidth={2.5} strokeLinecap="round" />
            <line x1={l3[0]} y1={l3[1]} x2={l4[0]} y2={l4[1]} stroke="#22B3A3" strokeOpacity={0.4} strokeWidth={2.5} strokeLinecap="round" />
        </g>
    );
};

const Shelf = ({ x, y }) => (
    <g>
        <Box x={x} y={y} w={26} d={70} z0={26} h={96} t="#FBFCFE" l="#E9ECF3" r="#DDE1EC" stroke="#D5DAE6" />
        {[58, 90].map((sz) => (
            <line key={sz} x1={P(x + 13, y - 30, sz)[0]} y1={P(x + 13, y - 30, sz)[1]} x2={P(x + 13, y + 30, sz)[0]} y2={P(x + 13, y + 30, sz)[1]} stroke="#D5DAE6" strokeWidth={1} />
        ))}
        <Box x={x + 6} y={y - 18} w={14} d={10} z0={58} h={20} t="#8A4BB0" l="#74409A" r="#653889" />
        <Box x={x + 6} y={y + 12} w={14} d={12} z0={26} h={28} t="#35B3A3" l="#2AA79A" r="#249789" />
    </g>
);

const Pendant = ({ x, y }) => {
    const top = P(x, y, 200);
    const bot = P(x, y, 150);
    const s1 = P(x - 12, y, 150);
    const s2 = P(x + 12, y, 150);
    const s3 = P(x + 5, y, 134);
    const s4 = P(x - 5, y, 134);
    const [gx, gy] = P(x, y, 112);
    return (
        <g>
            <line x1={top[0]} y1={top[1]} x2={bot[0]} y2={bot[1]} stroke="#9AA3B8" strokeWidth={1.5} />
            <polygon points={pts(s1, s2, s3, s4)} fill="#1E3A5C" opacity={0.88} />
            <ellipse cx={gx} cy={gy} rx={26} ry={10} fill="url(#glowGrad)" opacity={0.6} />
        </g>
    );
};

const scene = { hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 0.45 } } };
const rise = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE } },
};

const ZoneMarker = ({ label, className, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE, delay }}
        data-testid={`zone-${label.toLowerCase().replace("-", "")}`}
        className={`absolute ${className} flex items-center gap-1.5 rounded-full border border-slate-200/80 bg-white/85 px-2.5 py-1 shadow-sm backdrop-blur-sm`}
    >
        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-brand-purple to-brand-teal" />
        <span className="text-[9px] font-bold tracking-[0.18em] text-ink/55">{label}</span>
    </motion.div>
);

const FloatCard = ({ icon: Icon, label, className, delay, dur, reduced, testId }) => (
    <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: EASE, delay }}
        className={`absolute ${className}`}
    >
        <motion.div
            animate={reduced ? {} : { y: [0, -4, 0] }}
            transition={{ duration: dur, repeat: Infinity, ease: "easeInOut" }}
            data-testid={testId}
            className="flex items-center gap-2.5 rounded-2xl border border-slate-100 bg-white px-3.5 py-2.5 shadow-[0_16px_36px_-18px_rgba(22,50,79,0.35)]"
        >
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-brand-purple/12 via-brand-blue/12 to-brand-teal/12 text-brand-blue">
                <Icon size={15} strokeWidth={2.4} />
            </span>
            <span className="whitespace-nowrap text-[12px] font-bold text-ink/80">{label}</span>
        </motion.div>
    </motion.div>
);

export default function HeroVisual() {
    const reducedMotion = useReducedMotion();
    const ref = useRef(null);
    // Pause the always-on float animations once the hero is scrolled out of view
    const inView = useInView(ref, { margin: "150px" });
    const reduced = reducedMotion || !inView;

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 55, damping: 16, mass: 0.6 });
    const sy = useSpring(my, { stiffness: 55, damping: 16, mass: 0.6 });
    const backX = useTransform(sx, [-1, 1], [3, -3]);
    const backY = useTransform(sy, [-1, 1], [2, -2]);
    const midX = useTransform(sx, [-1, 1], [5, -5]);
    const midY = useTransform(sy, [-1, 1], [4, -4]);
    const frontX = useTransform(sx, [-1, 1], [8, -8]);
    const frontY = useTransform(sy, [-1, 1], [6, -6]);
    const lightX = useTransform(sx, [-1, 1], [14, -14]);
    const lightY = useTransform(sy, [-1, 1], [10, -10]);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const scrollScale = useTransform(scrollYProgress, [0, 1], [1, 0.93]);
    const scrollY = useTransform(scrollYProgress, [0, 1], [0, -46]);

    const onMouseMove = (e) => {
        if (reduced) return;
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
        my.set(((e.clientY - rect.top) / rect.height) * 2 - 1);
    };

    return (
        <motion.div
            ref={ref}
            onMouseMove={onMouseMove}
            onMouseLeave={() => {
                mx.set(0);
                my.set(0);
            }}
            style={{ scale: scrollScale, y: scrollY, willChange: "transform" }}
            data-testid="isometric-workspace-canvas"
            className="relative mx-auto w-full max-w-[640px]"
        >
            {/* BACK LAYER — architectural blueprint */}
            <motion.div style={{ x: backX, y: backY }} className="absolute inset-0" aria-hidden="true">
                <svg viewBox="0 0 920 760" className="h-full w-full" fill="none">
                    <defs>
                        <pattern id="bpgrid" width="44" height="44" patternUnits="userSpaceOnUse">
                            <path d="M44 0H0V44" stroke="#2B5CAB" strokeOpacity="0.06" />
                        </pattern>
                    </defs>
                    <rect width="920" height="700" fill="url(#bpgrid)" />
                    <rect x="48" y="48" width="824" height="660" rx="20" stroke="#2B5CAB" strokeOpacity="0.1" strokeDasharray="3 8" />
                    <line x1="140" y1="722" x2="780" y2="722" stroke="#7E35A8" strokeOpacity="0.14" strokeWidth={1.5} />
                    <line x1="140" y1="716" x2="140" y2="728" stroke="#7E35A8" strokeOpacity="0.2" strokeWidth={1.5} />
                    <line x1="780" y1="716" x2="780" y2="728" stroke="#7E35A8" strokeOpacity="0.2" strokeWidth={1.5} />
                    <circle cx="852" cy="88" r="16" stroke="#22B3A3" strokeOpacity="0.2" strokeWidth={1.5} />
                    <circle cx="852" cy="88" r="3" fill="#22B3A3" fillOpacity={0.3} />
                </svg>
            </motion.div>

            {/* MID LAYER — the workspace scene */}
            <motion.div style={{ x: midX, y: midY }} className="relative">
                <motion.div
                    initial={{ opacity: 0, scale: 0.965 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
                >
                    <svg viewBox="0 0 920 760" className="h-auto w-full" role="img" aria-label="Animated visualization of the Work Island coworking space">
                        <defs>
                            <linearGradient id="rugGrad" gradientUnits="userSpaceOnUse" x1="200" y1="360" x2="460" y2="560">
                                <stop offset="0" stopColor="#7E35A8" stopOpacity="0.08" />
                                <stop offset="1" stopColor="#22B3A3" stopOpacity="0.08" />
                            </linearGradient>
                            <linearGradient id="windowGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0" stopColor="#E4EFFB" />
                                <stop offset="1" stopColor="#F7FBFF" />
                            </linearGradient>
                            <linearGradient id="lightGrad" gradientUnits="userSpaceOnUse" x1="260" y1="150" x2="620" y2="640">
                                <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.16" />
                                <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
                            </linearGradient>
                            <linearGradient id="screenGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0" stopColor="#9A5FC4" />
                                <stop offset="1" stopColor="#2BB3A3" />
                            </linearGradient>
                            <linearGradient id="glassGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0" stopColor="#CDE1FA" stopOpacity="0.38" />
                                <stop offset="1" stopColor="#AAC8F0" stopOpacity="0.12" />
                            </linearGradient>
                            <radialGradient id="glowGrad">
                                <stop offset="0" stopColor="#FFD896" stopOpacity="0.55" />
                                <stop offset="1" stopColor="#FFD896" stopOpacity="0" />
                            </radialGradient>
                            <radialGradient id="backdropGrad">
                                <stop offset="0" stopColor="#EDEFF7" stopOpacity="0.9" />
                                <stop offset="1" stopColor="#EDEFF7" stopOpacity="0" />
                            </radialGradient>
                            <filter id="soft" x="-60%" y="-60%" width="220%" height="220%">
                                <feGaussianBlur stdDeviation="7" />
                            </filter>
                        </defs>

                        <ellipse cx="460" cy="392" rx="430" ry="330" fill="url(#backdropGrad)" />

                        <motion.g variants={scene} initial="hidden" animate="show">
                            {/* architecture — corner walls, window, art */}
                            <motion.g variants={rise}>
                                <polygon points={pts(P(-290, -220, 26), P(290, -220, 26), P(290, -220, 160), P(-290, -220, 160))} fill="#F7F8FC" />
                                <polygon points={pts(P(-290, -220, 26), P(-290, 220, 26), P(-290, 220, 160), P(-290, -220, 160))} fill="#EEF0F6" />
                                {[-200, -110, -20, 70, 160, 250].map((wx) => (
                                    <line key={`wa${wx}`} x1={P(wx, -220, 26)[0]} y1={P(wx, -220, 26)[1]} x2={P(wx, -220, 160)[0]} y2={P(wx, -220, 160)[1]} stroke="#E4E8F1" strokeWidth={1} />
                                ))}
                                {[-140, -60, 20, 100, 180].map((wy) => (
                                    <line key={`wb${wy}`} x1={P(-290, wy, 26)[0]} y1={P(-290, wy, 26)[1]} x2={P(-290, wy, 160)[0]} y2={P(-290, wy, 160)[1]} stroke="#DFE4EE" strokeWidth={1} />
                                ))}
                                <line x1={P(-290, -220, 160)[0]} y1={P(-290, -220, 160)[1]} x2={P(290, -220, 160)[0]} y2={P(290, -220, 160)[1]} stroke="#FFFFFF" strokeWidth={1.6} opacity={0.9} />
                                <line x1={P(-290, -220, 160)[0]} y1={P(-290, -220, 160)[1]} x2={P(-290, 220, 160)[0]} y2={P(-290, 220, 160)[1]} stroke="#FFFFFF" strokeWidth={1.6} opacity={0.8} />
                                <line x1={P(-290, -220, 32)[0]} y1={P(-290, -220, 32)[1]} x2={P(290, -220, 32)[0]} y2={P(290, -220, 32)[1]} stroke="#DEE3EE" strokeWidth={2} />
                                <line x1={P(-290, -220, 32)[0]} y1={P(-290, -220, 32)[1]} x2={P(-290, 220, 32)[0]} y2={P(-290, 220, 32)[1]} stroke="#D9DEEA" strokeWidth={2} />
                                <polygon points={pts(P(150, -219, 66), P(280, -219, 66), P(280, -219, 146), P(150, -219, 146))} fill="url(#windowGrad)" stroke="#DDE3EE" strokeWidth={1.5} />
                                <line x1={P(215, -219, 66)[0]} y1={P(215, -219, 66)[1]} x2={P(215, -219, 146)[0]} y2={P(215, -219, 146)[1]} stroke="#D5DDE9" strokeWidth={1.2} />
                                <line x1={P(150, -219, 106)[0]} y1={P(150, -219, 106)[1]} x2={P(280, -219, 106)[0]} y2={P(280, -219, 106)[1]} stroke="#D5DDE9" strokeWidth={1.2} />
                                <polygon points={pts(P(-288.5, -40, 74), P(-288.5, 40, 74), P(-288.5, 40, 124), P(-288.5, -40, 124))} fill="#F8FAFD" stroke="#D8DEE9" strokeWidth={1.2} />
                                <circle cx={P(-288.5, -12, 99)[0]} cy={P(-288.5, -12, 99)[1]} r={8} fill="#7E35A8" opacity={0.35} />
                                <circle cx={P(-288.5, 14, 94)[0]} cy={P(-288.5, 14, 94)[1]} r={5} fill="#22B3A3" opacity={0.35} />
                            </motion.g>

                            {/* floor, light, shadows */}
                            <motion.g variants={rise}>
                                <Box x={0} y={0} w={580} d={440} z0={0} h={26} t="#F5F6FA" l="#E6E9F1" r="#DBDFEA" stroke="#E2E6F0" />
                                <line x1={P(-290, 220, 26)[0]} y1={P(-290, 220, 26)[1]} x2={P(290, 220, 26)[0]} y2={P(290, 220, 26)[1]} stroke="#FFFFFF" strokeOpacity={0.7} strokeWidth={1.5} />
                                <line x1={P(290, -220, 26)[0]} y1={P(290, -220, 26)[1]} x2={P(290, 220, 26)[0]} y2={P(290, 220, 26)[1]} stroke="#FFFFFF" strokeOpacity={0.7} strokeWidth={1.5} />
                                {[-145, 0, 145].map((t) => (
                                    <g key={t} stroke="#D9DEE9" strokeOpacity={0.55} strokeWidth={1}>
                                        <line x1={P(-280, t, 26.3)[0]} y1={P(-280, t, 26.3)[1]} x2={P(280, t, 26.3)[0]} y2={P(280, t, 26.3)[1]} />
                                        <line x1={P(t, -210, 26.3)[0]} y1={P(t, -210, 26.3)[1]} x2={P(t, 210, 26.3)[0]} y2={P(t, 210, 26.3)[1]} />
                                    </g>
                                ))}
                                <polygon points={pts(P(-170, -10, 26.6), P(90, -10, 26.6), P(90, 190, 26.6), P(-170, 190, 26.6))} fill="url(#rugGrad)" />
                                <polygon points={pts(P(-290, -220, 26.2), P(290, -220, 26.2), P(290, 220, 26.2), P(-290, 220, 26.2))} fill="url(#lightGrad)" />
                                <Shadow x={-40} y={85} rx={165} ry={46} o={0.05} />
                                <Shadow x={180} y={-60} rx={120} ry={36} o={0.05} />
                                <Shadow x={200} y={140} rx={75} ry={24} o={0.05} />
                                <Shadow x={-200} y={-125} rx={75} ry={24} o={0.04} />
                            </motion.g>

                            {/* whiteboard */}
                            <motion.g variants={rise}>
                                <Whiteboard />
                            </motion.g>

                            {/* cabin interior */}
                            <motion.g variants={rise}>
                                <ellipse cx={P(-200, -120, 26.4)[0]} cy={P(-200, -120, 26.4)[1]} rx={62} ry={20} fill="#16324F" opacity={0.05} filter="url(#soft)" />
                                <Shelf x={-265} y={-100} />
                                <Box x={-200} y={-120} w={96} d={40} z0={64} h={7} t="#E7CDA8" l="#C9A87F" r="#B8946C" stroke="#D9BC93" />
                                <Chair x={-200} y={-160} facing={1} />
                                <PersonSeated x={-200} y={-158} color="#16324F" />
                                <Laptop x={-215} y={-118} z={71} />
                                <Plant x={-145} y={-170} s={0.8} />
                            </motion.g>

                            {/* coworking cluster */}
                            <motion.g variants={rise}>
                                <Desk x={-40} y={40} />
                                <Desk x={-40} y={130} />
                                <Chair x={-100} y={2} facing={1} />
                                <Chair x={20} y={2} facing={1} />
                                <Chair x={-100} y={78} facing={-1} />
                                <Chair x={20} y={78} facing={-1} />
                                <Chair x={-100} y={92} facing={1} />
                                <Chair x={-40} y={168} facing={-1} />
                                <motion.g animate={reduced ? {} : { y: [0, -2.5, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}>
                                    <PersonSeated x={-100} y={2} color="#33415C" />
                                </motion.g>
                                <motion.g animate={reduced ? {} : { y: [0, -2.5, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}>
                                    <PersonSeated x={20} y={78} color="#526179" />
                                </motion.g>
                                <PersonSeated x={-40} y={168} color="#2AA79A" />
                                <Laptop x={-100} y={34} z={77} />
                                <Laptop x={20} y={46} z={77} flip />
                                <Laptop x={-40} y={136} z={77} flip />
                                <Lamp x={-140} y={44} z={77} />
                                <Cup x={64} y={36} z={77} />
                                <Box x={-132} y={126} w={16} d={11} z0={77} h={2.5} t="#8A4BB0" l="#74409A" r="#653889" />
                            </motion.g>

                            {/* meeting area */}
                            <motion.g variants={rise}>
                                <Chair x={140} y={-102} facing={1} />
                                <Chair x={220} y={-102} facing={1} />
                                <Chair x={140} y={-18} facing={-1} />
                                <Chair x={220} y={-18} facing={-1} />
                                <ellipse cx={P(180, -60, 26.4)[0]} cy={P(180, -60, 26.4)[1]} rx={100} ry={32} fill="#16324F" opacity={0.05} filter="url(#soft)" />
                                <Box x={180} y={-60} w={110} d={10} z0={26} h={40} t="#C4CCDD" l="#AEB7CB" r="#9AA5BE" />
                                <Box x={180} y={-60} w={150} d={64} z0={66} h={7} t="#FFFFFF" l="#E9ECF3" r="#DDE1EC" stroke="#D5DAE6" />
                                <PersonSeated x={140} y={-102} color="#3E4C66" />
                                <Laptop x={165} y={-66} z={73} />
                                <Cup x={205} y={-50} z={73} />
                                <motion.g animate={reduced ? {} : { y: [0, -3, 0] }} transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}>
                                    <PersonStanding x={258} y={-18} color="#16324F" />
                                </motion.g>
                                <Pendant x={140} y={-60} />
                                <Pendant x={220} y={-60} />
                            </motion.g>

                            {/* coffee bar */}
                            <motion.g variants={rise}>
                                <ellipse cx={P(200, 140, 26.4)[0]} cy={P(200, 140, 26.4)[1]} rx={70} ry={22} fill="#16324F" opacity={0.05} filter="url(#soft)" />
                                <Box x={200} y={140} w={110} d={40} z0={26} h={42} t="#E7CDA8" l="#C9A87F" r="#B8946C" stroke="#D9BC93" />
                                <Box x={168} y={130} w={24} d={18} z0={68} h={22} t="#5B6B8C" l="#4A5878" r="#3F4D6C" />
                                <Cup x={215} y={132} z={68} />
                                <Cup x={232} y={148} z={68} />
                            </motion.g>

                            {/* plants */}
                            <motion.g variants={rise}>
                                <Plant x={-280} y={-10} s={1.15} />
                                <Plant x={90} y={205} s={1.1} />
                                <Plant x={100} y={20} s={0.8} />
                            </motion.g>

                            {/* glass cabin */}
                            <motion.g variants={rise}>
                                <GlassPanel x1={-270} y1={-60} x2={-120} y2={-60} />
                                <GlassPanel x1={-120} y1={-190} x2={-120} y2={-60} />
                                {[-220, -170].map((fx) => {
                                    const b = P(fx, -60, 26);
                                    const t = P(fx, -60, 150);
                                    return <line key={fx} x1={b[0]} y1={b[1]} x2={t[0]} y2={t[1]} stroke="rgba(110,132,182,0.45)" strokeWidth={1.2} />;
                                })}
                                {[-145, -100].map((fy) => {
                                    const b = P(-120, fy, 26);
                                    const t = P(-120, fy, 150);
                                    return <line key={fy} x1={b[0]} y1={b[1]} x2={t[0]} y2={t[1]} stroke="rgba(110,132,182,0.45)" strokeWidth={1.2} />;
                                })}
                            </motion.g>
                        </motion.g>
                    </svg>
                </motion.div>
            </motion.div>

            {/* cursor-reactive brand light */}
            <motion.div style={{ x: lightX, y: lightY }} className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(560px 400px at 62% 30%, rgba(126,58,168,0.07), transparent 62%), radial-gradient(520px 380px at 30% 74%, rgba(34,179,163,0.06), transparent 62%)",
                    }}
                />
            </motion.div>

            {/* FRONT LAYER — zone markers + floating cards */}
            <motion.div style={{ x: frontX, y: frontY }} className="pointer-events-none absolute inset-0">
                <ZoneMarker label="CO-WORK" className="left-[30%] top-[41%]" delay={1.5} />
                <ZoneMarker label="CONNECT" className="left-[63%] top-[41%]" delay={1.9} />
                <ZoneMarker label="CREATE" className="left-[26%] top-[14%]" delay={2.3} />

                <FloatCard icon={Wifi} label="High-Speed Internet" className="bottom-[5%] left-[0%]" delay={1.7} dur={5.5} reduced={reduced} testId="float-card-internet" />
                <FloatCard icon={Building2} label="Premium Workspaces" className="right-[-1%] top-[58%]" delay={1.95} dur={6.5} reduced={reduced} testId="float-card-premium" />
            </motion.div>

            <p className="pointer-events-none absolute bottom-1 right-2 text-[10px] font-semibold tracking-wide text-ink/30">
                Conceptual workspace visualization
            </p>
        </motion.div>
    );
}
